import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCarousel from "../components/MediaCarousel";
import Nav from "../components/Nav";
import SEO from "../components/SEO";
import api, { formatApiError, mediaUrl } from "../lib/api";

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function ArtistMediaPage() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    const loadArtistMedia = async () => {
      try {
        let artistId = id;
        if (!UUID_RX.test(id)) {
          const slugResponse = await api.get(`/artists/slug/${id}`);
          artistId = slugResponse.data.user_id;
        }
        const response = await api.get(`/artists/${artistId}`);
        if (active) setArtist({
          ...response.data.profile,
          id: artistId,
          media: response.data.media || [],
        });
      } catch (loadError) {
        if (active) setError(formatApiError(loadError));
      }
    };
    loadArtistMedia();
    return () => { active = false; };
  }, [id]);

  const profileUrl = artist ? `/artist/${artist.slug || artist.id}` : `/artist/${id}`;
  const mediaItems = (artist?.media || [])
    .filter((item) => item.type !== "kyc")
    .map((item) => ({
      id: item.id,
      src: mediaUrl(item.id),
      isVideo: item.mime?.startsWith("video/"),
      title: item.title || "",
    }));

  if (!artist && !error) {
    return (
      <div>
        <SEO title="Artist Media" noindex path={`/artist/${id}/media`} />
        <Nav />
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <SEO title="Artist Media" noindex path={`/artist/${id}/media`} />
        <Nav />
        <main className="section container" style={{ minHeight: "50vh", textAlign: "center" }}>
          <h1>Artist media unavailable</h1>
          <p role="alert">{error}</p>
          <Link to={`/artist/${id}`} className="btn btn-gold">Back to artist profile</Link>
        </main>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title={`${artist.stage_name || "Artist"} — Photos and Videos`}
        description={`Explore performance photos and videos from ${artist.stage_name || "this artist"} on BookTalent.`}
        path={`/artist/${artist.slug || artist.id}/media`}
        image={artist.profile_image ? mediaUrl(artist.profile_image) : undefined}
      />
      <Nav />
      <main className="section container" data-testid="artist-media-page">
        <Link to={profileUrl} className="btn btn-ghost btn-sm">Back to {artist.stage_name || "artist"}</Link>
        <div className="section-header" style={{ marginTop: "var(--space-6)" }}>
          <h1>{artist.stage_name || "Artist"}: Photos and Videos</h1>
          <p className="text-muted">{mediaItems.length} media items</p>
        </div>
        {mediaItems.length === 0 ? (
          <div className="empty" data-testid="artist-media-empty">
            <div className="empty-title">No media yet</div>
            <Link to={profileUrl} className="btn btn-ghost btn-sm">Back to artist profile</Link>
          </div>
        ) : (
          <div className="media-grid" data-testid="artist-media-grid">
            {mediaItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className="media-tile"
                data-testid={`artist-media-${item.id}`}
                onClick={() => setLightbox({ items: mediaItems, idx: index })}
                aria-label={`Open ${item.isVideo ? "video" : "image"} ${index + 1} of ${mediaItems.length}`}
              >
                {item.isVideo ? <video src={item.src} muted /> : <img src={item.src} alt={item.title} />}
                <span className="media-tile-play" aria-hidden>{item.isVideo ? "" : "⤢"}</span>
              </button>
            ))}
          </div>
        )}
      </main>
      {lightbox && (
        <MediaCarousel
          lightbox={lightbox}
          onClose={() => setLightbox(null)}
          onChange={setLightbox}
        />
      )}
    </div>
  );
}
